<template>
  <div class="employee-bulk-upload" :class="{ 'nav-collapsed': isNavCollapsed }">
    <NavigationBar @nav-toggle="handleNavToggle" />

    <!-- Main Content -->
    <div class="dashboard-content">
      <div class="page-container">
        <!-- Modern page header -->
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">Bulk Upload Employees</h1>
              <p class="dashboard-subtitle">
                <span class="icon">🚀</span>
                Import multiple employee records quickly and securely
              </p>
            </div>
            <div class="header-actions">
              <button class="action-btn outline" @click="downloadTemplate">
                <i class="bx bx-download"></i> Download Template
              </button>
            </div>
          </div>
          
          <div class="breadcrumb-section">
            <div class="breadcrumb">
              <span class="breadcrumb-item" @click="gotTo('/')">
                <span class="icon">🏠</span>
                <span class="text">Home</span>
              </span>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-item" @click="gotTo('employee-dashboard')">
                <span class="text">Employees</span>
              </span>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Bulk Upload</span>
            </div>
          </div>
        </div>

          <!-- Upload Steps -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="hr-chart-card hr-premium-card">
                <div class="hr-chart-body">
                  <div class="d-flex justify-content-between">
                    <div class="step-item" :class="{'active': currentStep >= 1}">
                      <div class="step-icon">
                        <i class="mdi mdi-file-upload"></i>
                      </div>
                      <p class="mt-2">Upload File</p>
                    </div>
                    <div class="step-line" :class="{'active': currentStep >= 2}"></div>
                    <div class="step-item" :class="{'active': currentStep >= 2}">
                      <div class="step-icon">
                        <i class="mdi mdi-table-edit"></i>
                      </div>
                      <p class="mt-2">Map Fields</p>
                    </div>
                    <div class="step-line" :class="{'active': currentStep >= 3}"></div>
                    <div class="step-item" :class="{'active': currentStep >= 3}">
                      <div class="step-icon">
                        <i class="mdi mdi-eye-check"></i>
                      </div>
                      <p class="mt-2">Preview & Validate</p>
                    </div>
                    <div class="step-line" :class="{'active': currentStep >= 4}"></div>
                    <div class="step-item" :class="{'active': currentStep >= 4}">
                      <div class="step-icon">
                        <i class="mdi mdi-cloud-upload"></i>
                      </div>
                      <p class="mt-2">Import</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1: File Upload -->
          <div v-if="currentStep === 1" class="row">
            <div class="col-12">
              <div class="hr-chart-card hr-premium-card">
                <div class="hr-chart-header">
                  <h5 class="hr-chart-title mb-0">Step 1: Upload Employee File</h5>
                </div>
                <div class="hr-chart-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <div class="mb-4">
                        <label class="form-label">Upload File</label>
                        <div class="dropzone-area" @drop="handleDrop" @dragover.prevent @dragenter.prevent>
                          <input type="file" ref="fileInput" @change="handleFileSelect" accept=".xlsx,.xls,.csv,.txt" hidden>
                          <div v-if="!selectedFile" class="text-center p-5" @click="$refs.fileInput.click()">
                            <i class="mdi mdi-cloud-upload-outline font-size-48 text-primary"></i>
                            <h5 class="mt-3">Drop files here or click to upload</h5>
                            <p class="text-muted">Supported formats: Excel (.xlsx, .xls), CSV (.csv), or Text (.txt)</p>
                            <p class="text-info">Format will be auto-detected</p>
                          </div>
                          <div v-else class="d-flex align-items-center justify-content-between p-4 bg-light rounded">
                            <div>
                              <i :class="getFileIcon(selectedFile.name)" class="font-size-24 me-2"></i>
                              <span>{{ selectedFile.name }}</span>
                              <span class="text-muted ms-2">({{ formatFileSize(selectedFile.size) }})</span>
                              <span v-if="detectedFormat" class="badge bg-info ms-2">{{ detectedFormat }} detected</span>
                            </div>
                            <button @click="removeFile" class="btn btn-sm btn-danger">
                              <i class="mdi mdi-delete"></i> Remove
                            </button>
                          </div>
                        </div>
                      </div>

                      <div v-if="fileData.length > 0" class="alert alert-info">
                        <i class="mdi mdi-information-outline me-2"></i>
                        File contains <strong>{{ fileData.length }}</strong> records
                        <span v-if="fileData.length > 1000" class="text-warning ms-2">
                          (Will be processed in {{ Math.ceil(fileData.length / batchSize) }} batches)
                        </span>
                      </div>

                      <div class="d-flex justify-content-between">
                        <button @click="downloadTemplate" class="action-btn outline">
                          <i class="mdi mdi-download me-1"></i> Download Template
                        </button>
                        <button @click="processFile" :disabled="!selectedFile" class="action-btn">
                          Next Step <i class="mdi mdi-arrow-right ms-1"></i>
                        </button>
                      </div>
                    </div>

                    <div class="col-lg-6">
                      <div class="alert alert-info">
                        <h5 class="alert-heading">Instructions</h5>
                        <ul class="mb-0">
                          <li>File format is automatically detected</li>
                          <li>Large files will be processed in batches</li>
                          <li>Names can be in a single column (will be auto-split)</li>
                          <li>For expatriates, use passport number if no national ID</li>
                          <li>Date format: YYYY-MM-DD</li>
                        </ul>
                      </div>

                      <div class="hr-chart-card hr-premium-card bg-light">
                        <div class="hr-chart-body">
                          <h6 class="hr-chart-title">Required Fields</h6>
                          <div class="row">
                            <div class="col-6">
                              <ul class="list-unstyled mb-0">
                                <li><i class="mdi mdi-check text-success"></i> Employee Number</li>
                                <li><i class="mdi mdi-check text-success"></i> Name (or First + Last)</li>
                                <li><i class="mdi mdi-check text-success"></i> Email</li>
                              </ul>
                            </div>
                            <div class="col-6">
                              <ul class="list-unstyled mb-0">
                                <li><i class="mdi mdi-check text-success"></i> Phone Number</li>
                                <li><i class="mdi mdi-check text-success"></i> ID Number</li>
                                <li><i class="mdi mdi-check text-success"></i> Department</li>
                              </ul>
                            </div>
                          </div>
                          <div class="mt-3">
                            <p class="text-muted mb-0"><small><strong>Note:</strong> Other fields like Gender, DOB, Employment Type, and Hire Date will use default values if not provided.</small></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Field Mapping -->
          <div v-if="currentStep === 2" class="row">
            <div class="col-12">
              <div class="hr-chart-card hr-premium-card">
                <div class="hr-chart-header">
                  <h5 class="hr-chart-title mb-0">Step 2: Map Fields</h5>
                </div>
                <div class="hr-chart-body">
                  <!-- Name Handling Section -->
                  <div v-if="hasFullNameColumn" class="alert alert-warning mb-4">
                    <h6><i class="mdi mdi-account-details me-2"></i>Name Column Detected</h6>
                    <p>We detected a column with full names. How would you like to split them?</p>
                    <div class="row mt-3">
                      <div class="col-md-4">
                        <label class="form-label">Full Name Column</label>
                        <select v-model="nameColumnMapping.fullNameColumn" class="form-select">
                          <option value="">-- Select Column --</option>
                          <option v-for="col in fileColumns" :key="col" :value="col">{{ col }}</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label">Split Strategy</label>
                        <select v-model="nameColumnMapping.nameSplitStrategy" class="form-select" @change="previewNameSplit">
                          <option value="auto">Auto Detect</option>
                          <option value="firstLast">First Last (2 parts)</option>
                          <option value="firstMiddleLast">First Middle Last (3 parts)</option>
                          <option value="manual">Manual Mapping</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label">Preview</label>
                        <div v-if="nameSplitPreview" class="text-muted">
                          <small>
                            First: {{ nameSplitPreview.first }}<br>
                            Middle: {{ nameSplitPreview.middle }}<br>
                            Last: {{ nameSplitPreview.last }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="table-responsive">
                    <table class="hr-table-modern">
                      <thead>
                      <tr>
                        <th width="30%">System Field</th>
                        <th width="30%">Your File Column</th>
                        <th width="30%">Sample Data</th>
                        <th width="10%">Status</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="field in visibleMappingFields" :key="field.systemField">
                        <td>
                          {{ field.label }}
                          <span v-if="field.required" class="text-danger">*</span>
                          <span v-if="field.autoMapped" class="badge bg-info ms-2">Auto</span>
                        </td>
                        <td>
                          <select v-model="field.mappedColumn"
                                  class="form-select form-select-sm"
                                  @change="updateMapping(field)"
                                  :disabled="field.disabled">
                            <option value="">-- Select Column --</option>
                            <option v-for="col in fileColumns" :key="col" :value="col">{{ col }}</option>
                          </select>
                        </td>
                        <td>
                            <span v-if="field.mappedColumn && sampleData[field.mappedColumn]" class="text-muted">
                              {{ sampleData[field.mappedColumn] }}
                            </span>
                          <span v-else-if="field.derivedValue" class="text-info">
                              {{ field.derivedValue }}
                            </span>
                        </td>
                        <td class="text-center">
                          <i v-if="field.mappedColumn || field.derivedValue" class="mdi mdi-check-circle text-success"></i>
                          <i v-else-if="field.required && !field.conditional" class="mdi mdi-alert-circle text-danger"></i>
                          <i v-else class="mdi mdi-minus-circle text-muted"></i>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="d-flex justify-content-between mt-4">
                    <button @click="currentStep = 1" class="btn btn-secondary">
                      <i class="mdi mdi-arrow-left me-1"></i> Previous
                    </button>
                    <div>
                      <button @click="autoMapFields" class="btn btn-outline-info me-2">
                        <i class="mdi mdi-auto-fix me-1"></i> Auto Map
                      </button>
                      <button @click="validateMapping" class="action-btn">
                        Next Step <i class="mdi mdi-arrow-right ms-1"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Preview & Validate -->
          <div v-if="currentStep === 3" class="row">
            <div class="col-12">
              <div class="hr-chart-card hr-premium-card">
                <div class="hr-chart-header d-flex justify-content-between align-items-center">
                  <h5 class="hr-chart-title mb-0">Step 3: Preview & Validate</h5>
                  <div>
                    <span class="badge bg-success me-2">Valid: {{ validRecords }}</span>
                    <span class="badge bg-danger me-2">Invalid: {{ invalidRecords }}</span>
                    <span class="badge bg-info">Total: {{ previewData.length }}</span>
                  </div>
                </div>
                <div class="hr-chart-body">
                  <!-- Department Mapping Section -->
                  <div class="mb-4">
                    <h6 class="mb-3">Department Mapping</h6>
                    <div class="alert alert-info">
                      <i class="mdi mdi-information-outline me-2"></i>
                      Map departments from your file to existing system departments
                    </div>
                    <div class="row">
                      <div v-for="(mapping, dept) in departmentMappings" :key="dept" class="col-md-4 mb-3">
                        <label class="form-label">{{ dept }}</label>
                        <select v-model="departmentMappings[dept]" @change="updateDepartmentMapping(dept)" class="form-select form-select-sm">
                          <option value="">-- Select Department --</option>
                          <option v-for="sysDept in systemDepartments" :key="sysDept.id" :value="sysDept.id">
                            {{ sysDept.departmentName }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <!-- Preview Table -->
                  <div class="table-responsive">
                    <table class="hr-table-modern">
                      <thead>
                      <tr>
                        <th width="30">
                          <input type="checkbox" v-model="selectAll" @change="toggleSelectAll" class="form-check-input">
                        </th>
                        <th>Status</th>
                        <th>Emp No</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>ID Number</th>
                        <th>Gender</th>
                        <th>DOB</th>
                        <th>Nationality</th>
                        <th>Type</th>
                        <th>Department</th>
                        <th>Issues</th>
                        <th>Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(row, index) in paginatedPreviewData" :key="index"
                          :class="{'table-danger': row.hasErrors, 'table-warning': row.hasWarnings}">
                        <td>
                          <input type="checkbox" v-model="row.selected" class="form-check-input" :disabled="row.hasErrors">
                        </td>
                        <td>
                          <span v-if="row.hasErrors" class="badge bg-danger">Invalid</span>
                          <span v-else-if="row.hasWarnings" class="badge bg-warning">Warning</span>
                          <span v-else class="badge bg-success">Valid</span>
                        </td>
                        <td>{{ row.empNo }}</td>
                        <td>{{ row.firstName }} {{ row.middleName }} {{ row.lastName }}</td>
                        <td>{{ row.email }}</td>
                        <td>{{ row.nationalId || '-' }}</td>
                        <td>{{ row.gender }}</td>
                        <td>{{ formatDate(row.dateOfBirth) }}</td>
                        <td>{{ row.nationality || 'Kenyan' }}</td>
                        <td>
                            <span :class="getEmploymentTypeClass(row.employmentType)">
                              {{ row.employmentType }}
                            </span>
                        </td>
                        <td>
                          <select v-model="row.departmentID" @change="updateRecordDepartment(row)" class="form-select form-select-sm">
                            <option value="">-- Select --</option>
                            <option v-for="dept in systemDepartments" :key="dept.id" :value="dept.id">
                              {{ dept.departmentName }}
                            </option>
                          </select>
                        </td>
                        <td>
                          <ul v-if="row.errors && row.errors.length" class="mb-0 ps-3">
                            <li v-for="error in row.errors" :key="error" class="text-danger small">{{ error }}</li>
                          </ul>
                          <ul v-if="row.warnings && row.warnings.length" class="mb-0 ps-3">
                            <li v-for="warning in row.warnings" :key="warning" class="text-warning small">{{ warning }}</li>
                          </ul>
                        </td>
                        <td>
                          <button @click="editRecord(index)" class="btn btn-sm btn-info me-1">
                            <i class="mdi mdi-pencil"></i>
                          </button>
                          <button @click="removeRecord(index)" class="btn btn-sm btn-danger">
                            <i class="mdi mdi-delete"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div class="row mt-3">
                    <div class="col-sm-12 col-md-5">
                      <div class="dataTables_info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, previewData.length) }} of
                        {{ previewData.length }} entries
                      </div>
                    </div>
                    <div class="col-sm-12 col-md-7">
                      <div class="dataTables_paginate paging_simple_numbers">
                        <ul class="pagination justify-content-end mb-0">
                          <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <a class="page-link" href="javascript:void(0)" @click="currentPage--">Previous</a>
                          </li>
                          <li v-for="page in totalPages" :key="page"
                              class="page-item" :class="{ active: page === currentPage }">
                            <a class="page-link" href="javascript:void(0)" @click="currentPage = page">{{ page }}</a>
                          </li>
                          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <a class="page-link" href="javascript:void(0)" @click="currentPage++">Next</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between mt-4">
                    <button @click="currentStep = 2" class="btn btn-secondary">
                      <i class="mdi mdi-arrow-left me-1"></i> Previous
                    </button>
                    <button @click="proceedToImport" :disabled="invalidRecords > 0" class="action-btn">
                      Proceed to Import <i class="mdi mdi-arrow-right ms-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: Import -->
          <div v-if="currentStep === 4" class="row">
            <div class="col-12">
              <div class="hr-chart-card hr-premium-card">
                <div class="hr-chart-header">
                  <h5 class="hr-chart-title mb-0">Step 4: Import Employees</h5>
                </div>
                <div class="hr-chart-body">
                  <div v-if="!importStarted" class="text-center py-5">
                    <i class="mdi mdi-account-multiple-plus font-size-48 text-primary"></i>
                    <h4 class="mt-4">Ready to Import</h4>
                    <p class="text-muted">
                      You are about to import <strong>{{ validRecords }}</strong> employee records
                      <span v-if="totalBatches > 1">in <strong>{{ totalBatches }}</strong> batches</span>.
                    </p>
                    <div class="alert alert-warning d-inline-block">
                      <i class="mdi mdi-alert-outline me-2"></i>
                      This action cannot be undone. Please ensure all data is correct.
                    </div>
                    <div class="mt-4">
                      <button @click="currentStep = 3" class="btn btn-secondary me-2">
                        <i class="mdi mdi-arrow-left me-1"></i> Review Again
                      </button>
                      <button @click="startImport" class="btn btn-success">
                        <i class="mdi mdi-cloud-upload me-1"></i> Start Import
                      </button>
                    </div>
                  </div>

                  <div v-else>
                    <!-- Import Progress -->
                    <div v-if="importing">
                      <div class="text-center">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Importing...</span>
                        </div>
                        <h5 class="mt-3">Importing Employees...</h5>
                        <p class="text-muted">
                          Processing batch {{ currentBatch }} of {{ totalBatches }}
                        </p>
                      </div>
                      <div class="progress mt-4">
                        <div class="progress-bar progress-bar-striped progress-bar-animated"
                             :style="`width: ${importProgress}%`">
                          {{ importProgress }}%
                        </div>
                      </div>
                      <p class="text-center mt-2">
                        Processed {{ processedRecords }} of {{ validRecords }} records
                      </p>
                    </div>

                    <!-- Import Results -->
                    <div v-if="importComplete">
                      <div class="text-center">
                        <i v-if="successCount > 0 && failedCount === 0" class="mdi mdi-check-circle font-size-48 text-success"></i>
                        <i v-else-if="failedCount > 0" class="mdi mdi-alert-circle font-size-48 text-danger"></i>
                        <i v-else class="mdi mdi-information-circle font-size-48 text-warning"></i>
                        <h4 class="mt-4">
                          {{ successCount > 0 && failedCount === 0 ? 'Import Successful!' :
                            failedCount > 0 ? 'Import Completed with Errors' :
                                'Import Completed' }}
                        </h4>
                      </div>

                      <div class="row mt-4">
                        <div class="col-md-4">
                          <div class="hr-chart-card hr-premium-card bg-success bg-gradient text-white">
                            <div class="hr-chart-body">
                              <h4 style="color: white">{{ successCount }}</h4>
                              <p class="mb-0">Successfully Imported</p>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="hr-chart-card hr-premium-card bg-danger bg-gradient text-white">
                            <div class="hr-chart-body">
                              <h4 style="color: white">{{ failedCount }}</h4>
                              <p class="mb-0">Failed</p>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <div class="hr-chart-card hr-premium-card bg-warning bg-gradient text-white">
                            <div class="hr-chart-body">
                              <h4 style="color: white">{{ skippedCount }}</h4>
                              <p class="mb-0">Skipped</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Success Details -->
                      <div v-if="successfulRecords.length > 0" class="mt-4">
                        <div class="hr-chart-card hr-premium-card">
                          <div class="hr-chart-header bg-success text-white">
                            <h6 class="mb-0 text-white">
                              <i class="mdi mdi-check-circle me-2"></i>
                              Successfully Imported Employees ({{ successfulRecords.length }})
                            </h6>
                          </div>
                          <div class="hr-chart-body">
                            <div class="table-responsive" style="max-height: 250px; overflow-y: auto;">
                              <table class="hr-table-modern table-sm">
                                <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Employee Number</th>
                                  <th>Name</th>
                                  <th>Email</th>
                                  <th>Department</th>
                                  <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(record, index) in successfulRecords" :key="index">
                                  <td>{{ index + 1 }}</td>
                                  <td>{{ record.empNo }}</td>
                                  <td>{{ record.name }}</td>
                                  <td>{{ record.email }}</td>
                                  <td>{{ record.department }}</td>
                                  <td><span class="badge bg-success">Imported</span></td>
                                </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Skipped Details -->
                      <div v-if="skippedRecords.length > 0" class="mt-4">
                        <div class="hr-chart-card hr-premium-card">
                          <div class="hr-chart-header bg-warning">
                            <h6 class="mb-0">
                              <i class="mdi mdi-alert-circle me-2"></i>
                              Skipped Records ({{ skippedRecords.length }})
                            </h6>
                          </div>
                          <div class="hr-chart-body">
                            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                              <table class="hr-table-modern table-sm">
                                <thead>
                                <tr>
                                  <th>Row</th>
                                  <th>Employee</th>
                                  <th>Reason</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(record, index) in skippedRecords" :key="index">
                                  <td>{{ record.row }}</td>
                                  <td>{{ record.employee }}</td>
                                  <td>
                                      <span class="text-warning">
                                        <i class="mdi mdi-information-outline me-1"></i>
                                        {{ record.reason }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Failed Details -->
                      <div v-if="failedRecords.length > 0" class="mt-4">
                        <div class="hr-chart-card hr-premium-card">
                          <div class="hr-chart-header bg-danger text-white">
                            <h6 class="mb-0">
                              <i class="mdi mdi-close-circle me-2"></i>
                              Failed Records ({{ failedRecords.length }})
                            </h6>
                          </div>
                          <div class="hr-chart-body">
                            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                              <table class="hr-table-modern table-sm">
                                <thead>
                                <tr>
                                  <th>Row</th>
                                  <th>Employee</th>
                                  <th>Error</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(record, index) in failedRecords" :key="index">
                                  <td>{{ record.row }}</td>
                                  <td>{{ record.employee }}</td>
                                  <td class="text-danger">
                                    <i class="mdi mdi-alert-circle me-1"></i>
                                    {{ record.reason }}
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mt-4 text-center">
                        <button @click="downloadDetailedReport" class="btn btn-outline-primary me-2">
                          <i class="mdi mdi-download me-1"></i> Download Detailed Report
                        </button>
                        <button @click="gotTo('employee-register')" class="action-btn">
                          <i class="mdi mdi-view-dashboard me-1"></i>Go to Employees
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Edit Record Modal -->
          <div class="modal fade" id="editRecordModal" tabindex="-1">
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Edit Employee Record</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)"></button>
                </div>
                <div class="modal-body">
                  <div v-if="editingRecord" class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Employee No <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.empNo" type="text" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">First Name <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.firstName" type="text" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Middle Name</label>
                      <input v-model="editingRecord.middleName" type="text" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Last Name <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.lastName" type="text" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Work Email <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.email" type="email" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Personal Email</label>
                      <input v-model="editingRecord.personalEmail" type="email" class="form-control" placeholder="personal@email.com">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Phone <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.phone" type="text" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">National ID</label>
                      <input v-model="editingRecord.nationalId" type="text" class="form-control">
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">Gender <span class="text-danger">*</span></label>
                      <select v-model="editingRecord.gender" class="form-select">
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Date of Birth <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.dateOfBirth" type="date" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Nationality</label>
                      <input v-model="editingRecord.nationality" type="text" class="form-control" placeholder="Kenyan">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Employment Type <span class="text-danger">*</span></label>
                      <select v-model="editingRecord.employmentType" class="form-select">
                        <option value="Permanent">Permanent</option>
                        <option value="Contract">Contract</option>
                        <option value="Temporary">Temporary</option>
                        <option value="Intern">Intern</option>
                        <option value="Consultant">Consultant</option>
                        <option value="Expatriate">Expatriate</option>
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Hire Date <span class="text-danger">*</span></label>
                      <input v-model="editingRecord.hireDate" type="date" class="form-control">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Department</label>
                      <select v-model="editingRecord.departmentID" class="form-select">
                        <option value="">-- Select --</option>
                        <option v-for="dept in systemDepartments" :key="dept.id" :value="dept.id">
                          {{ dept.departmentName }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Position</label>
                      <input v-model="editingRecord.positionTitle" type="text" class="form-control" placeholder="e.g. Software Developer">
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
                  <button type="button" @click="saveEditedRecord" class="action-btn">Save Changes</button>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export default {
  name: "EmployeeBulkUpload",
  components: { NavigationBar },
  data() {
    return {
      currentStep: 1,
      selectedFile: null,
      detectedFormat: null,
      fileColumns: [],
      fileData: [],
      sampleData: {},
      systemDepartments: [],
      departmentMappings: {},
      nameColumnMapping: {
        hasFullName: false,
        fullNameColumn: '',
        nameSplitStrategy: 'auto'
      },
      nameSplitPreview: null,
      mappingFields: [
        { systemField: 'empNo', label: 'Employee Number', required: true, mappedColumn: '' },
        { systemField: 'fullName', label: 'Full Name', required: false, mappedColumn: '', isSpecial: true },
        { systemField: 'firstName', label: 'First Name', required: true, mappedColumn: '', derivedValue: null },
        { systemField: 'middleName', label: 'Middle Name', required: false, mappedColumn: '', derivedValue: null },
        { systemField: 'lastName', label: 'Last Name', required: true, mappedColumn: '', derivedValue: null },
        { systemField: 'email', label: 'Work Email', required: true, mappedColumn: '' },
        { systemField: 'personalEmail', label: 'Personal Email', required: false, mappedColumn: '' },
        { systemField: 'phone', label: 'Phone', required: true, mappedColumn: '' },
        { systemField: 'nationalId', label: 'National ID', required: true, mappedColumn: '' },
        { systemField: 'gender', label: 'Gender', required: false, mappedColumn: '' },
        { systemField: 'dateOfBirth', label: 'Date of Birth', required: false, mappedColumn: '' },
        { systemField: 'nationality', label: 'Nationality', required: false, mappedColumn: '' },
        { systemField: 'employmentType', label: 'Employment Type', required: false, mappedColumn: '' },
        { systemField: 'hireDate', label: 'Hire Date', required: false, mappedColumn: '' },
        { systemField: 'department', label: 'Department', required: true, mappedColumn: '' },
        { systemField: 'positionTitle', label: 'Position', required: false, mappedColumn: '' }
      ],
      previewData: [],
      validRecords: 0,
      invalidRecords: 0,
      selectAll: false,
      currentPage: 1,
      itemsPerPage: 10,
      editingRecord: null,
      editingIndex: -1,

      // Import phase
      importStarted: false,
      importing: false,
      importComplete: false,
      importSuccess: false,
      importProgress: 0,
      processedRecords: 0,
      successCount: 0,
      failedCount: 0,
      skippedCount: 0,
      importErrors: [],

      // New arrays for detailed records
      successfulRecords: [],
      skippedRecords: [],
      failedRecords: [],

      // Batch processing
      batchSize: 50,
      totalBatches: 0,
      currentBatch: 0,
      hasFullNameColumn: false
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.previewData.length / this.itemsPerPage);
    },
    paginatedPreviewData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.previewData.slice(start, end);
    },
    visibleMappingFields() {
      // If we have a full name column and using auto/specific strategy
      if (this.nameColumnMapping.hasFullName && this.nameColumnMapping.nameSplitStrategy !== 'manual') {
        // Show Full Name field and hide individual name fields
        return this.mappingFields.filter(f => {
          if (f.systemField === 'fullName') return true; // Show full name
          if (['firstName', 'middleName', 'lastName'].includes(f.systemField)) return false; // Hide individual names
          return true; // Show everything else
        });
      }

      // Otherwise show individual name fields and hide full name
      return this.mappingFields.filter(f => f.systemField !== 'fullName');
    }
  },
  mounted() {
    this.loadDepartments();
    // Add a test to check if API is accessible
    this.testAPIConnection();
  },
  methods: {
    async testAPIConnection() {
      console.log('Testing API connection...');
      try {
        const data = new FormData();
        data.append("function", "getDepartments");
        const res = await execute(data, authUrl);
        console.log('API connection test successful:', res ? 'Connected' : 'Failed');
      } catch (error) {
        console.error('API connection test failed:', error);
      }
    },

    async loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      try {
        const res = await execute(data, authUrl);
        // Check if res and res.data exist
        if (res && res.data) {
          if (res.data.success) {
            // Handle different possible response structures
            this.systemDepartments = res.data.data?.departments || res.data.departments || [];
          } else {
            console.warn("Failed to load departments:", res.data.message || "Unknown error");
            this.systemDepartments = [];
          }
        } else {
          console.warn("Invalid response structure from getDepartments");
          this.systemDepartments = [];
        }
      } catch (error) {
        console.error("Failed to load departments:", error);
        this.systemDepartments = [];
        // Don't block the user from continuing
        this.$toast?.warning("Could not load departments. You can continue without department mapping.");
      }
    },

    getFileIcon(filename) {
      const ext = filename.split('.').pop().toLowerCase();
      if (['xlsx', 'xls'].includes(ext)) return 'mdi mdi-file-excel text-success';
      if (ext === 'csv') return 'mdi mdi-file-delimited text-info';
      if (ext === 'txt') return 'mdi mdi-file-document text-warning';
      return 'mdi mdi-file text-muted';
    },

    detectFileFormat(filename, content) {
      const ext = filename.split('.').pop().toLowerCase();

      // Check by extension first
      if (['xlsx', 'xls'].includes(ext)) return 'Excel';
      if (ext === 'csv') return 'CSV';
      if (ext === 'txt') {
        // Check if it's tab-delimited or comma-delimited
        const firstLine = content.split('\n')[0];
        if (firstLine.includes('\t')) return 'TSV';
        if (firstLine.includes(',')) return 'CSV';
      }

      return 'Unknown';
    },

    handleDrop(e) {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    },

    handleFile(file) {
      this.selectedFile = file;
      // Auto-detect format
      this.detectedFormat = this.detectFileFormat(file.name, '');

      // Start processing immediately
      this.processFilePreview();
    },

    async processFilePreview() {
      if (!this.selectedFile) return;

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          let data;
          const filename = this.selectedFile.name;
          const ext = filename.split('.').pop().toLowerCase();

          if (['xlsx', 'xls'].includes(ext)) {
            // Excel file - with proper date handling
            const workbook = XLSX.read(e.target.result, {
              type: 'binary',
              cellDates: true,  // Parse dates as Date objects
              dateNF: 'yyyy-mm-dd'  // Format for dates
            });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Convert with date formatting
            data = XLSX.utils.sheet_to_json(worksheet, {
              dateNF: 'yyyy-mm-dd',
              raw: false  // Get formatted strings instead of raw values
            });

            // Additional date fixing for common date fields
            data = data.map(row => {
              const processedRow = { ...row };

              // List of common date field names
              const dateFields = ['Date of Birth', 'dateOfBirth', 'DOB', 'Birth Date',
                'Hire Date', 'hireDate', 'Start Date', 'Join Date',
                'Employment Date', 'Joining Date'];

              Object.keys(processedRow).forEach(key => {
                // Check if this is a date field
                const isDateField = dateFields.some(df =>
                    key.toLowerCase().includes(df.toLowerCase().replace(/\s+/g, ''))
                );

                if (isDateField && processedRow[key]) {
                  // Try to parse and format the date
                  processedRow[key] = this.formatExcelDate(processedRow[key]);
                }
              });

              return processedRow;
            });

            this.detectedFormat = 'Excel';
          } else {
            // CSV or text file
            const text = e.target.result;
            const delimiter = text.includes('\t') ? '\t' : ',';

            const result = Papa.parse(text, {
              header: true,
              delimiter: delimiter,
              skipEmptyLines: true
            });
            data = result.data;
            this.detectedFormat = delimiter === '\t' ? 'TSV' : 'CSV';
          }

          if (data.length === 0) {
            if (this.$toast && this.$toast.error) {
              this.$toast.error("The file appears to be empty.");
            } else {
              alert("The file appears to be empty.");
            }
            return;
          }

          this.fileData = data;
          this.fileColumns = Object.keys(data[0]);
          this.sampleData = data[0];

          // Check for full name column immediately after loading
          this.detectNameColumns();

          // Extract unique departments for mapping
          const deptColumns = ['department', 'Department', 'DEPARTMENT', 'dept', 'Dept'];
          let deptColumn = null;
          for (let col of deptColumns) {
            if (this.fileColumns.some(fc => fc.toLowerCase() === col.toLowerCase())) {
              deptColumn = this.fileColumns.find(fc => fc.toLowerCase() === col.toLowerCase());
              break;
            }
          }

          if (deptColumn) {
            const uniqueDepts = [...new Set(data.map(row => row[deptColumn]).filter(Boolean))];
            this.departmentMappings = {};

            // Try to auto-map departments only if system departments are loaded
            if (this.systemDepartments && this.systemDepartments.length > 0) {
              uniqueDepts.forEach(dept => {
                const systemDept = this.systemDepartments.find(sysDept => {
                  const deptLower = dept.toString().toLowerCase().trim();
                  const sysDeptLower = sysDept.departmentName.toLowerCase().trim();
                  return deptLower === sysDeptLower;
                });

                if (systemDept) {
                  this.departmentMappings[dept] = systemDept.id;
                } else {
                  this.departmentMappings[dept] = '';
                }
              });

              console.log('System departments available:', this.systemDepartments.map(d => d.departmentName));
              console.log('Extracted departments from file:', uniqueDepts);
              console.log('Auto-mapped departments:', this.departmentMappings);
            } else {
              // If departments not loaded yet, just initialize empty mappings
              uniqueDepts.forEach(dept => {
                this.departmentMappings[dept] = '';
              });
              console.log('System departments not loaded yet, skipping auto-mapping');
            }
          }

          if (this.$toast && this.$toast.success) {
            this.$toast.success(`${this.detectedFormat} file loaded with ${data.length} records`);
          } else {
            console.log(`${this.detectedFormat} file loaded with ${data.length} records`);
          }
        } catch (error) {
          if (this.$toast && this.$toast.error) {
            this.$toast.error("Error processing file: " + error.message);
          } else {
            alert("Error processing file: " + error.message);
          }
        }
      };

      if (['xlsx', 'xls'].includes(this.selectedFile.name.split('.').pop().toLowerCase())) {
        reader.readAsBinaryString(this.selectedFile);
      } else {
        reader.readAsText(this.selectedFile);
      }
    },

    formatExcelDate(value) {
      // Handle various date input formats
      if (!value) return '';

      // If already in YYYY-MM-DD format, return as is
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      // Handle Excel serial numbers (days since 1900-01-01)
      if (typeof value === 'number' || !isNaN(value)) {
        const excelEpoch = new Date(1900, 0, 1);
        const days = parseInt(value) - 2; // Excel has a leap year bug for 1900
        const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
        return this.formatDateToYYYYMMDD(date);
      }

      // Try to parse as date string
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return this.formatDateToYYYYMMDD(date);
      }

      // Try various date formats
      const formats = [
        /(\d{1,2})\/(\d{1,2})\/(\d{4})/,  // MM/DD/YYYY or DD/MM/YYYY
        /(\d{1,2})-(\d{1,2})-(\d{4})/,     // MM-DD-YYYY or DD-MM-YYYY
        /(\d{4})\/(\d{1,2})\/(\d{1,2})/,   // YYYY/MM/DD
      ];

      for (const format of formats) {
        const match = value.toString().match(format);
        if (match) {
          // Assume MM/DD/YYYY for ambiguous formats
          let year, month, day;
          if (match[1].length === 4) {
            [, year, month, day] = match;
          } else {
            [, month, day, year] = match;
            // Swap if day > 12 (must be DD/MM/YYYY)
            if (parseInt(month) > 12 && parseInt(day) <= 12) {
              [month, day] = [day, month];
            }
          }
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }

      console.warn('Could not parse date:', value);
      return value; // Return original if can't parse
    },

    formatDateToYYYYMMDD(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    detectNameColumns() {
      // Check for full name column - expanded list of possible name columns
      const nameColumns = ['name', 'Name', 'NAME', 'fullname', 'FullName', 'Full Name',
        'Employee Name', 'Staff Name', 'Person Name', 'Worker Name'];

      for (let col of this.fileColumns) {
        // Direct match check (case-insensitive)
        const colLower = col.trim().toLowerCase();
        const isNameColumn = nameColumns.some(nc =>
            colLower === nc.toLowerCase() ||
            colLower === nc.toLowerCase().replace(/\s+/g, '') ||
            colLower.replace(/\s+/g, '') === nc.toLowerCase().replace(/\s+/g, '')
        );

        if (isNameColumn) {
          this.hasFullNameColumn = true;
          this.nameColumnMapping.hasFullName = true;
          this.nameColumnMapping.fullNameColumn = col;

          // Also map it to the fullName field
          const fullNameField = this.mappingFields.find(f => f.systemField === 'fullName');
          if (fullNameField) {
            fullNameField.mappedColumn = col;
          }

          // Preview name split
          this.previewNameSplit();
          break;
        }
      }

      // If no full name column found, check if we have separate name columns
      if (!this.hasFullNameColumn) {
        const hasFirstName = this.fileColumns.some(col =>
            col.toLowerCase().includes('first') && col.toLowerCase().includes('name')
        );
        const hasLastName = this.fileColumns.some(col =>
            col.toLowerCase().includes('last') && col.toLowerCase().includes('name')
        );

        if (!hasFirstName && !hasLastName) {
          // Check if there's a column simply called "Name"
          const nameCol = this.fileColumns.find(col =>
              col.trim().toLowerCase() === 'name'
          );
          if (nameCol) {
            this.hasFullNameColumn = true;
            this.nameColumnMapping.hasFullName = true;
            this.nameColumnMapping.fullNameColumn = nameCol;

            const fullNameField = this.mappingFields.find(f => f.systemField === 'fullName');
            if (fullNameField) {
              fullNameField.mappedColumn = nameCol;
            }

            this.previewNameSplit();
          }
        }
      }
    },

    splitName(fullName, strategy = 'auto') {
      if (!fullName) return { first: '', middle: '', last: '' };

      // Clean the name - remove extra spaces and handle special characters
      const cleanName = fullName.trim().replace(/\s+/g, ' ');
      const parts = cleanName.split(/\s+/);

      if (strategy === 'auto') {
        // Auto-detect based on number of parts
        if (parts.length === 1) {
          // Single name - check if it contains a dot (like "Kelvin.n.njuguna")
          if (parts[0].includes('.')) {
            const dotParts = parts[0].split('.');
            if (dotParts.length >= 3) {
              return {
                first: dotParts[0],
                middle: dotParts[1],
                last: dotParts.slice(2).join('.')
              };
            } else if (dotParts.length === 2) {
              return {
                first: dotParts[0],
                middle: '',
                last: dotParts[1]
              };
            }
          }
          // Single word name
          return { first: parts[0], middle: '', last: '' };
        } else if (parts.length === 2) {
          return { first: parts[0], middle: '', last: parts[1] };
        } else if (parts.length === 3) {
          return { first: parts[0], middle: parts[1], last: parts[2] };
        } else if (parts.length > 3) {
          // Take first as first name, last as last name, rest as middle
          return {
            first: parts[0],
            middle: parts.slice(1, -1).join(' '),
            last: parts[parts.length - 1]
          };
        } else {
          return { first: fullName, middle: '', last: '' };
        }
      } else if (strategy === 'firstLast') {
        if (parts.length === 1 && parts[0].includes('.')) {
          const dotParts = parts[0].split('.');
          return {
            first: dotParts[0] || '',
            middle: '',
            last: dotParts.slice(1).join('.') || ''
          };
        }
        return {
          first: parts[0] || '',
          middle: '',
          last: parts.slice(1).join(' ') || ''
        };
      } else if (strategy === 'firstMiddleLast') {
        if (parts.length === 1 && parts[0].includes('.')) {
          const dotParts = parts[0].split('.');
          return {
            first: dotParts[0] || '',
            middle: dotParts.length > 2 ? dotParts[1] : '',
            last: dotParts[dotParts.length - 1] || ''
          };
        }
        return {
          first: parts[0] || '',
          middle: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
          last: parts[parts.length - 1] || ''
        };
      }

      return { first: '', middle: '', last: '' };
    },

    previewNameSplit() {
      if (this.nameColumnMapping.fullNameColumn && this.sampleData[this.nameColumnMapping.fullNameColumn]) {
        const sampleName = this.sampleData[this.nameColumnMapping.fullNameColumn];
        this.nameSplitPreview = this.splitName(sampleName, this.nameColumnMapping.nameSplitStrategy);

        // Update derived values for name fields
        const firstNameField = this.mappingFields.find(f => f.systemField === 'firstName');
        const middleNameField = this.mappingFields.find(f => f.systemField === 'middleName');
        const lastNameField = this.mappingFields.find(f => f.systemField === 'lastName');

        if (firstNameField) firstNameField.derivedValue = this.nameSplitPreview.first;
        if (middleNameField) middleNameField.derivedValue = this.nameSplitPreview.middle;
        if (lastNameField) lastNameField.derivedValue = this.nameSplitPreview.last;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.fileData = [];
      this.fileColumns = [];
      this.detectedFormat = null;
      this.$refs.fileInput.value = '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    processFile() {
      if (!this.selectedFile || this.fileData.length === 0) {
        this.$toast?.error("Please upload and process a file first") || alert("Please upload and process a file first");
        return;
      }

      // Detect name columns before moving to mapping step
      this.detectNameColumns();

      this.currentStep = 2;
      // Auto-map fields on entering step 2 - with safety check
      this.$nextTick(() => {
        if (this.fileColumns && this.fileColumns.length > 0) {
          this.autoMapFields();
        }
      });
    },

    autoMapFields() {
      // First, detect and map the full name column
      this.detectNameColumns();

      this.mappingFields.forEach(field => {
        if (field.systemField === 'fullName') {
          // Already handled in detectNameColumns
          return;
        }

        const possibleNames = this.getPossibleColumnNames(field.systemField, field.label);
        for (let col of this.fileColumns) {
          // Normalize column name: trim spaces, convert to lowercase, remove extra spaces
          const normalizedCol = col.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\s/g, '');

          // Check if any possible name matches
          const matched = possibleNames.some(name => {
            const normalizedName = name.toLowerCase().replace(/\s+/g, '').replace(/[\s_-]/g, '');
            return normalizedCol.includes(normalizedName) || normalizedName.includes(normalizedCol);
          });

          if (matched) {
            field.mappedColumn = col;
            field.autoMapped = true;
            break;
          }
        }
      });

      // Special handling for specific columns

      // ID Number column
      const idColumn = this.fileColumns.find(col =>
          col.toLowerCase().replace(/\s+/g, '').includes('idnumber') ||
          col.toLowerCase() === 'id number' ||
          col.trim().toLowerCase() === 'id number'
      );
      if (idColumn) {
        const nationalIdField = this.mappingFields.find(f => f.systemField === 'nationalId');
        if (nationalIdField && !nationalIdField.mappedColumn) {
          nationalIdField.mappedColumn = idColumn;
          nationalIdField.autoMapped = true;
        }
      }

      // Department column
      const deptColumn = this.fileColumns.find(col =>
          col.trim().toLowerCase() === 'department' ||
          col.trim().toLowerCase() === 'dept' ||
          col.toLowerCase().includes('department')
      );
      if (deptColumn) {
        const deptField = this.mappingFields.find(f => f.systemField === 'department');
        if (deptField && !deptField.mappedColumn) {
          deptField.mappedColumn = deptColumn;
          deptField.autoMapped = true;
        }
      }

      // Employee Number with space
      const empNoColumn = this.fileColumns.find(col =>
          col.trim().toLowerCase().replace(/\s+/g, '') === 'employeenumber' ||
          col.trim().toLowerCase() === 'employee number'
      );
      if (empNoColumn) {
        const empNoField = this.mappingFields.find(f => f.systemField === 'empNo');
        if (empNoField && !empNoField.mappedColumn) {
          empNoField.mappedColumn = empNoColumn;
          empNoField.autoMapped = true;
        }
      }

      // Phone Number with space
      const phoneColumn = this.fileColumns.find(col =>
          col.trim().toLowerCase().replace(/\s+/g, '') === 'phonenumber' ||
          col.trim().toLowerCase() === 'phone number'
      );
      if (phoneColumn) {
        const phoneField = this.mappingFields.find(f => f.systemField === 'phone');
        if (phoneField && !phoneField.mappedColumn) {
          phoneField.mappedColumn = phoneColumn;
          phoneField.autoMapped = true;
        }
      }

      if (this.$toast && this.$toast.success) {
        this.$toast.success("Auto-mapping completed!");
      } else {
        console.log("Auto-mapping completed!");
        console.log("Mapped fields:", this.mappingFields.filter(f => f.mappedColumn).map(f => `${f.label} -> ${f.mappedColumn}`));
      }
    },

    getPossibleColumnNames(systemField, label) {
      const mapping = {
        'empNo': ['employee number', 'emp no', 'employee no', 'empno', 'employee_number', 'staff no', 'staff number', 'employeenumber'],
        'firstName': ['first name', 'firstname', 'first_name', 'fname'],
        'middleName': ['middle name', 'middlename', 'middle_name', 'mname'],
        'lastName': ['last name', 'lastname', 'last_name', 'surname', 'lname'],
        'email': ['email', 'work email', 'email address', 'email_address', 'e-mail', 'mail', 'emailaddress'],
        'phone': ['phone', 'phone number', 'telephone', 'mobile', 'contact', 'tel', 'phonenumber', 'phone no'],
        'nationalId': ['national id', 'id number', 'national_id', 'id_number', 'nin', 'id no', 'idnumber', 'id'],
        'gender': ['gender', 'sex'],
        'dateOfBirth': ['date of birth', 'birth date', 'dob', 'birthday', 'birth_date', 'dateofbirth'],
        'nationality': ['nationality', 'citizen', 'citizenship'],
        'employmentType': ['employment type', 'contract type', 'employee type', 'type', 'employment_type', 'employmenttype'],
        'hireDate': ['hire date', 'join date', 'start date', 'employment date', 'joining date', 'hiredate'],
        'department': ['department', 'dept', 'division', 'section', 'dept.'],
        'personalEmail': ['personal email', 'personal_email', 'personalemail', 'personal e-mail', 'personal mail'],
        'positionTitle': ['position', 'position title', 'position_title', 'job title', 'job title', 'designation', 'role']
      };
      return mapping[systemField] || [label];
    },

    updateMapping(field) {
      if (field.mappedColumn && this.fileData.length > 0) {
        this.sampleData[field.mappedColumn] = this.fileData[0][field.mappedColumn];
      }
    },

    validateMapping() {
      // Check if using full name or separate fields
      if (this.nameColumnMapping.hasFullName && this.nameColumnMapping.fullNameColumn) {
        // Using full name column - skip validation for separate name fields
        const requiredFields = this.mappingFields.filter(f =>
            f.required && !['firstName', 'lastName'].includes(f.systemField)
        );
        const unmappedRequired = requiredFields.filter(f => !f.mappedColumn);

        if (unmappedRequired.length > 0) {
          const fieldNames = unmappedRequired.map(f => f.label).join(', ');
          if (this.$toast && this.$toast.error) {
            this.$toast.error(`Please map all required fields: ${fieldNames}`);
          } else {
            alert(`Please map all required fields: ${fieldNames}`);
          }
          return;
        }
      } else {
        // Using separate name fields
        const requiredFields = this.mappingFields.filter(f =>
            f.required && f.systemField !== 'fullName'
        );
        const unmappedRequired = requiredFields.filter(f => !f.mappedColumn && !f.derivedValue);

        if (unmappedRequired.length > 0) {
          const fieldNames = unmappedRequired.map(f => f.label).join(', ');
          if (this.$toast && this.$toast.error) {
            this.$toast.error(`Please map all required fields: ${fieldNames}`);
          } else {
            alert(`Please map all required fields: ${fieldNames}`);
          }
          return;
        }
      }

      this.preparePreviewData();
      this.currentStep = 3;
    },

    preparePreviewData() {
      this.previewData = [];
      this.validRecords = 0;
      this.invalidRecords = 0;

      // First, extract unique departments and create auto-mappings
      const deptColumn = this.mappingFields.find(f => f.systemField === 'department')?.mappedColumn;
      if (deptColumn) {
        const uniqueDepts = [...new Set(this.fileData.map(row => row[deptColumn]).filter(Boolean))];

        // Reset department mappings
        this.departmentMappings = {};

        uniqueDepts.forEach(dept => {
          // Try to auto-map to system departments
          const systemDept = this.systemDepartments.find(sysDept => {
            const deptLower = dept.toString().toLowerCase().trim();
            const sysDeptLower = sysDept.departmentName.toLowerCase().trim();

            // Direct match or close match
            return deptLower === sysDeptLower ||
                sysDeptLower.includes(deptLower) ||
                deptLower.includes(sysDeptLower) ||
                // Handle DEVELOPMENT vs DEVELOPMENT
                (deptLower === 'development' && sysDeptLower === 'development') ||
                // Handle variations
                (deptLower.replace(/[^a-z]/g, '') === sysDeptLower.replace(/[^a-z]/g, ''));
          });

          if (systemDept) {
            this.departmentMappings[dept] = systemDept.id;
            console.log(`Auto-mapped department: ${dept} -> ${systemDept.departmentName} (ID: ${systemDept.id})`);
          } else {
            this.departmentMappings[dept] = '';
            console.log(`Could not auto-map department: ${dept}`);
          }
        });
      }

      this.fileData.forEach((row, index) => {
        const mappedRow = {
          selected: true,
          hasErrors: false,
          hasWarnings: false,
          errors: [],
          warnings: [],
          _originalIndex: index
        };

        // Handle name splitting if using full name column
        if (this.nameColumnMapping.hasFullName && this.nameColumnMapping.fullNameColumn) {
          const fullName = row[this.nameColumnMapping.fullNameColumn];
          const splitNames = this.splitName(fullName, this.nameColumnMapping.nameSplitStrategy);
          mappedRow.firstName = splitNames.first;
          mappedRow.middleName = splitNames.middle;
          mappedRow.lastName = splitNames.last;
        }

        // Map other fields
        this.mappingFields.forEach(field => {
          if (field.systemField === 'fullName') return;

          // Skip name fields if already handled by full name
          if (this.nameColumnMapping.hasFullName &&
              ['firstName', 'middleName', 'lastName'].includes(field.systemField)) {
            return;
          }

          if (field.mappedColumn) {
            mappedRow[field.systemField] = row[field.mappedColumn];

            // Debug logging for email field
            if (field.systemField === 'email' && mappedRow.empNo === 'CSPL0015') {
              console.log('CSPL0015 email mapping:', {
                mappedColumn: field.mappedColumn,
                rawValue: row[field.mappedColumn],
                mappedValue: mappedRow[field.systemField]
              });
            }
          }
        });

        // Map department BEFORE validation
        if (mappedRow.department && this.departmentMappings[mappedRow.department]) {
          mappedRow.departmentID = this.departmentMappings[mappedRow.department];
          const dept = this.systemDepartments.find(d => d.id === mappedRow.departmentID);
          if (dept) {
            mappedRow.departmentName = dept.departmentName;
          }
        }

        // Validate data (this will now see the departmentID if it's set)
        this.validateRecord(mappedRow);

        if (mappedRow.hasErrors) {
          this.invalidRecords++;
          mappedRow.selected = false;
        } else {
          this.validRecords++;
        }

        this.previewData.push(mappedRow);
      });

      // Calculate batches for import
      this.totalBatches = Math.ceil(this.validRecords / this.batchSize);

      console.log('Department mappings:', this.departmentMappings);
      console.log('Preview data prepared. Valid:', this.validRecords, 'Invalid:', this.invalidRecords);
    },

    validateRecord(record) {
      // Set defaults for missing non-required fields
      if (!record.gender) {
        record.gender = 'Other'; // Default gender
      }

      if (!record.dateOfBirth) {
        record.dateOfBirth = '1990-01-01'; // Default DOB
      }

      if (!record.hireDate) {
        record.hireDate = new Date().toISOString().split('T')[0]; // Today's date as default
      }

      if (!record.employmentType) {
        record.employmentType = 'Permanent'; // Default employment type
      }

      if (!record.nationality) {
        record.nationality = 'Kenyan'; // Default nationality
      }

      // REQUIRED FIELD VALIDATIONS

      // Employee number validation (REQUIRED)
      if (!record.empNo) {
        record.hasErrors = true;
        record.errors.push("Employee number is required");
      }

      // Name validation (REQUIRED)
      if (!record.firstName || !record.lastName) {
        record.hasErrors = true;
        record.errors.push("First name and last name are required");
      }

      // Email validation (REQUIRED)
      if (!record.email) {
        record.hasErrors = true;
        record.errors.push("Email is required");
      } else {
        // Trim email before validation
        const originalEmail = record.email;
        record.email = record.email.trim();

        if (!this.isValidEmail(record.email)) {
          console.log(`Email validation failed for: "${originalEmail}" -> "${record.email}"`);
          record.hasErrors = true;
          record.errors.push("Invalid email format");
        }
      }

      // Phone validation (REQUIRED)
      if (!record.phone) {
        record.hasErrors = true;
        record.errors.push("Phone number is required");
      } else if (!this.isValidPhone(record.phone)) {
        record.hasWarnings = true;
        record.warnings.push("Phone number format may be incorrect");
      }

      // ID validation (REQUIRED - National ID only)
      if (!record.nationalId) {
        record.hasErrors = true;
        record.errors.push("National ID is required");
      }

      // Department validation - check if it's mapped in departmentMappings
      if (record.department) {
        // Check if this department has a mapping
        if (this.departmentMappings && this.departmentMappings[record.department]) {
          // Department is mapped, set the ID
          record.departmentID = this.departmentMappings[record.department];
        } else if (!record.departmentID) {
          // No mapping found and no ID set
          record.hasWarnings = true;
          record.warnings.push("Department not mapped to system department");
        }
      } else if (!record.department && !record.departmentID) {
        record.hasErrors = true;
        record.errors.push("Department is required");
      }

      // OPTIONAL FIELD VALIDATIONS (only validate if provided)

      // Date validation for dateOfBirth if provided and not default
      if (record.dateOfBirth && record.dateOfBirth !== '1990-01-01') {
        const formattedDOB = this.formatExcelDate(record.dateOfBirth);
        record.dateOfBirth = formattedDOB;
        if (!this.isValidDate(formattedDOB)) {
          record.hasWarnings = true;
          record.warnings.push("Invalid date of birth format (using default)");
          record.dateOfBirth = '1990-01-01';
        }
      }

      // Date validation for hireDate if provided
      if (record.hireDate) {
        const formattedHireDate = this.formatExcelDate(record.hireDate);
        record.hireDate = formattedHireDate;
        if (!this.isValidDate(formattedHireDate)) {
          record.hasWarnings = true;
          record.warnings.push("Invalid hire date format (using today)");
          record.hireDate = new Date().toISOString().split('T')[0];
        }
      }

      // Gender validation if provided
      const validGenders = ['Male', 'Female', 'Other'];
      if (record.gender && !validGenders.includes(record.gender)) {
        record.hasWarnings = true;
        record.warnings.push("Invalid gender value (using 'Other')");
        record.gender = 'Other';
      }

      // Employment type validation if provided
      const validTypes = ['Permanent', 'Contract', 'Temporary', 'Intern', 'Consultant', 'Expatriate'];
      if (record.employmentType && !validTypes.includes(record.employmentType)) {
        record.hasWarnings = true;
        record.warnings.push("Invalid employment type (using 'Permanent')");
        record.employmentType = 'Permanent';
      }

      // Check for duplicate employee numbers in current batch
      const duplicates = this.previewData.filter(r => r.empNo === record.empNo);
      if (duplicates.length > 1) {
        record.hasWarnings = true;
        record.warnings.push("Duplicate Employee Number in file");
      }
    },

    updateDepartmentMapping(dept) {
      // Update all preview records with this department
      const deptId = this.departmentMappings[dept];
      const systemDept = this.systemDepartments.find(d => d.id === deptId);

      this.previewData.forEach(record => {
        if (record.department === dept) {
          record.departmentID = deptId;
          if (systemDept) {
            record.departmentName = systemDept.departmentName;
          }

          // Clear department warning if it was set
          if (record.warnings) {
            record.warnings = record.warnings.filter(w => !w.includes('Department not mapped'));
            record.hasWarnings = record.warnings.length > 0;
          }

          // Update status based on errors/warnings
          if (!record.hasErrors && !record.hasWarnings) {
            record.selected = true;
          }
        }
      });

      // Recalculate valid/invalid counts
      this.validRecords = this.previewData.filter(r => !r.hasErrors).length;
      this.invalidRecords = this.previewData.filter(r => r.hasErrors).length;

      console.log(`Updated department mapping: ${dept} -> ${systemDept?.departmentName || 'None'}`);
    },

    updateRecordDepartment(record) {
      // Update individual record's department
      const systemDept = this.systemDepartments.find(d => d.id === record.departmentID);
      if (systemDept) {
        record.departmentName = systemDept.departmentName;

        // Clear department warning
        if (record.warnings) {
          record.warnings = record.warnings.filter(w => !w.includes('Department not mapped'));
          record.hasWarnings = record.warnings.length > 0;
        }

        // Update selection status
        if (!record.hasErrors && !record.hasWarnings) {
          record.selected = true;
        }
      }

      // Recalculate valid/invalid counts
      this.validRecords = this.previewData.filter(r => !r.hasErrors).length;
      this.invalidRecords = this.previewData.filter(r => r.hasErrors).length;
    },

    isValidEmail(email) {
      if (!email) return false;

      // Clean the email first
      const cleanEmail = email.toString().trim();

      // Check basic structure
      if (!cleanEmail.includes('@') || !cleanEmail.includes('.')) {
        return false;
      }

      // More comprehensive email validation regex
      // This handles most common email formats including dots, hyphens, underscores
      const re = /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
      const isValid = re.test(cleanEmail);

      if (!isValid) {
        console.log(`Email validation details for "${cleanEmail}":`, {
          hasAt: cleanEmail.includes('@'),
          hasDot: cleanEmail.includes('.'),
          regexTest: re.test(cleanEmail),
          length: cleanEmail.length
        });
      }

      return isValid;
    },

    isValidPhone(phone) {
      const re = /^[\d\s\-\+\(\)]+$/;
      return re.test(phone) && phone.replace(/\D/g, '').length >= 9;
    },

    isValidDate(date) {
      if (!date) return false;

      // Check if it's in YYYY-MM-DD format
      if (typeof date === 'string') {
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(date)) return false;

        // Check if it's a valid date
        const parsedDate = new Date(date + 'T00:00:00');
        return !isNaN(parsedDate.getTime());
      }

      return !isNaN(Date.parse(date));
    },

    toggleSelectAll() {
      this.previewData.forEach(row => {
        if (!row.hasErrors) {
          row.selected = this.selectAll;
        }
      });
    },

    editRecord(index) {
      this.editingIndex = (this.currentPage - 1) * this.itemsPerPage + index;
      this.editingRecord = { ...this.previewData[this.editingIndex] };
      if (typeof bootstrap !== 'undefined') {
        const modal = new bootstrap.Modal(document.getElementById('editRecordModal'));
        modal.show();
      } else {
        // Fallback if bootstrap is not available
        console.warn('Bootstrap modal not available');
      }
    },

    saveEditedRecord() {
      if (this.editingIndex >= 0) {
        // Clear all errors and warnings before re-validation
        this.editingRecord.errors = [];
        this.editingRecord.warnings = [];
        this.editingRecord.hasErrors = false;
        this.editingRecord.hasWarnings = false;

        // Clean email if edited
        if (this.editingRecord.email) {
          this.editingRecord.email = this.editingRecord.email.trim();
        }

        // Re-validate the record
        this.validateRecord(this.editingRecord);

        // Update the record in the preview data
        this.previewData[this.editingIndex] = { ...this.editingRecord };

        // If errors were fixed, make it selectable
        if (!this.editingRecord.hasErrors) {
          this.previewData[this.editingIndex].selected = true;
        }

        // Recalculate counts
        this.validRecords = this.previewData.filter(r => !r.hasErrors).length;
        this.invalidRecords = this.previewData.filter(r => r.hasErrors).length;

        if (typeof bootstrap !== 'undefined') {
          const modal = bootstrap.Modal.getInstance(document.getElementById('editRecordModal'));
          if (modal) modal.hide();
        }

        if (this.$toast && this.$toast.success) {
          this.$toast.success("Record updated successfully!");
        } else {
          console.log("Record updated successfully!");
        }

        console.log('Updated record:', this.previewData[this.editingIndex]);
      }
    },

    removeRecord(index) {
      const actualIndex = (this.currentPage - 1) * this.itemsPerPage + index;
      if (confirm("Are you sure you want to remove this record?")) {
        this.previewData.splice(actualIndex, 1);
        this.validRecords = this.previewData.filter(r => !r.hasErrors).length;
        this.invalidRecords = this.previewData.filter(r => r.hasErrors).length;
        this.totalBatches = Math.ceil(this.validRecords / this.batchSize);
        if (this.$toast && this.$toast.success) {
          this.$toast.success("Record removed!");
        } else {
          console.log("Record removed!");
        }
      }
    },

    proceedToImport() {
      if (this.invalidRecords > 0) {
        if (this.$toast && this.$toast.error) {
          this.$toast.error("Please fix all errors before importing.");
        } else {
          alert("Please fix all errors before importing.");
        }
        return;
      }

      const unmappedDepts = Object.keys(this.departmentMappings).filter(
          dept => !this.departmentMappings[dept]
      );

      if (unmappedDepts.length > 0) {
        if (!confirm(`Some departments are not mapped. Records with unmapped departments will be skipped. Continue?`)) {
          return;
        }
      }

      this.currentStep = 4;
    },

    async startImport() {
      console.log('Starting import process...');
      console.log('Preview data:', this.previewData);
      console.log('Valid records count:', this.validRecords);
      console.log('Invalid records count:', this.invalidRecords);

      this.importStarted = true;
      this.importing = true;
      this.importProgress = 0;
      this.processedRecords = 0;
      this.successCount = 0;
      this.failedCount = 0;
      this.skippedCount = 0;
      this.importErrors = [];

      // Clear detailed records arrays
      this.successfulRecords = [];
      this.skippedRecords = [];
      this.failedRecords = [];

      this.currentBatch = 0;

      const selectedRecords = this.previewData.filter(r => r.selected && !r.hasErrors);
      console.log('Selected records for import:', selectedRecords);
      console.log('Records with errors:', this.previewData.filter(r => r.hasErrors));

      const totalRecords = selectedRecords.length;
      const batchId = `batch_${Date.now()}`;

      // Recalculate total batches based on selected records
      this.totalBatches = Math.ceil(totalRecords / this.batchSize);

      console.log(`Processing ${totalRecords} records in ${this.totalBatches} batches (batch size: ${this.batchSize})`);

      if (totalRecords === 0) {
        console.error('No valid records to import');
        console.log('Checking why no records selected:');
        console.log('- Total preview records:', this.previewData.length);
        console.log('- Records with selected=true:', this.previewData.filter(r => r.selected).length);
        console.log('- Records without errors:', this.previewData.filter(r => !r.hasErrors).length);

        if (this.$toast && this.$toast.error) {
          this.$toast.error("No valid records to import. Please check for errors in the preview.");
        } else {
          alert("No valid records to import. Please check for errors in the preview.");
        }
        this.importing = false;
        this.importComplete = true;
        return;
      }

      // Process in batches
      for (let i = 0; i < selectedRecords.length; i += this.batchSize) {
        this.currentBatch++;
        const batch = selectedRecords.slice(i, i + this.batchSize);

        console.log(`Processing batch ${this.currentBatch}/${this.totalBatches} with ${batch.length} records`);

        try {
          await this.importBatch(batch, i, batchId);
        } catch (error) {
          console.error('Batch import error:', error);
        }

        this.importProgress = Math.round((this.processedRecords / totalRecords) * 100);
      }

      console.log('Import process completed');
      this.importing = false;
      this.importComplete = true;
      this.importSuccess = this.failedCount === 0;
    },

    async importBatch(batch, startIndex, batchId) {
      console.log('Preparing batch data for API call...');

      // Check if execute function exists
      if (typeof execute !== 'function') {
        console.error('Execute function is not available. Check API import.');
        throw new Error('API execute function not available');
      }

      // Clean the batch data - remove UI-specific properties
      const cleanBatch = batch.map(record => {
        const cleanRecord = {};
        // Only include the actual employee data fields
        const fieldsToInclude = [
          'empNo', 'firstName', 'middleName', 'lastName', 'email', 'personalEmail', 'phone',
          'nationalId', 'gender', 'dateOfBirth', 'nationality',
          'employmentType', 'hireDate', 'departmentID', 'departmentName',
          'positionTitle', 'position_id'
        ];

        fieldsToInclude.forEach(field => {
          if (record[field] !== undefined && record[field] !== null) {
            cleanRecord[field] = record[field];
          }
        });

        // Ensure departmentName is included
        if (record.departmentID && !cleanRecord.departmentName) {
          // Try to get department name from system departments
          const dept = this.systemDepartments.find(d => d.id === record.departmentID);
          if (dept) {
            cleanRecord.departmentName = dept.departmentName;
          }
        }

        // If no departmentName but has department field, use that
        if (!cleanRecord.departmentName && record.department) {
          cleanRecord.departmentName = record.department;
        }

        return cleanRecord;
      });

      const data = new FormData();
      data.append("function", "bulkUploadEmployees");
      data.append("employees", JSON.stringify(cleanBatch));
      data.append("batchId", batchId);
      data.append("totalBatches", String(this.totalBatches));
      data.append("currentBatch", String(this.currentBatch));

      console.log('Sending batch to API:', {
        function: "bulkUploadEmployees",
        batchId: batchId,
        totalBatches: this.totalBatches,
        currentBatch: this.currentBatch,
        recordCount: cleanBatch.length,
        sampleRecord: cleanBatch[0]
      });

      console.log('authUrl:', authUrl);

      try {
        const response = await execute(data, authUrl);

        console.log('API Response received:', response);

        if (response && response.data) {
          if (response.data.success) {
            const results = response.data.data.results || {};

            console.log('Processing successful response:', results);

            // Process successful records
            if (results.success && Array.isArray(results.success)) {
              this.successCount += results.success.length;
              results.success.forEach(success => {
                // Get the actual record from the batch
                const record = batch[success.row - 1] || batch[0];
                this.successfulRecords.push({
                  empNo: record.empNo,
                  name: `${record.firstName} ${record.middleName || ''} ${record.lastName}`.trim(),
                  email: record.email,
                  department: record.departmentName || record.department || 'N/A'
                });
              });
            }

            // Process failed records
            if (results.failed && Array.isArray(results.failed)) {
              this.failedCount += results.failed.length;
              results.failed.forEach(fail => {
                this.failedRecords.push({
                  row: startIndex + fail.row,
                  employee: fail.employee,
                  reason: fail.reason || 'Unknown error'
                });
              });
            }

            // Process skipped records
            if (results.skipped && Array.isArray(results.skipped)) {
              this.skippedCount += results.skipped.length;
              results.skipped.forEach(skip => {
                this.skippedRecords.push({
                  row: startIndex + skip.row,
                  employee: skip.employee,
                  reason: skip.reason || 'Record skipped'
                });
              });
            }

            this.processedRecords += batch.length;
          } else {
            // Handle batch failure
            console.error('API returned failure:', response.data.message || 'Unknown error');

            batch.forEach((record, index) => {
              this.failedCount++;
              this.failedRecords.push({
                row: startIndex + index + 1,
                employee: `${record.firstName} ${record.lastName}`,
                reason: response.data.message || 'Batch import failed'
              });
            });
            this.processedRecords += batch.length;
          }
        } else {
          throw new Error('Invalid API response structure');
        }
      } catch (error) {
        // Handle network or other errors
        console.error('Import batch error:', error);

        batch.forEach((record, index) => {
          this.failedCount++;
          this.failedRecords.push({
            row: startIndex + index + 1,
            employee: `${record.firstName} ${record.lastName}`,
            reason: error.message || 'Network error'
          });
        });
        this.processedRecords += batch.length;
      }

      // Add small delay between batches to avoid overwhelming server
      await new Promise(resolve => setTimeout(resolve, 500));
    },

    getEmploymentTypeClass(type) {
      const classes = {
        'Permanent': 'badge bg-success',
        'Contract': 'badge bg-info',
        'Temporary': 'badge bg-warning',
        'Intern': 'badge bg-secondary',
        'Consultant': 'badge bg-primary',
        'Expatriate': 'badge bg-danger'
      };
      return classes[type] || 'badge bg-secondary';
    },

    formatDate(date) {
      if (!date) return '';
      try {
        return new Date(date).toLocaleDateString('en-GB');
      } catch {
        return date;
      }
    },

    downloadTemplate() {
      // Columns match Create Employee form: all required and optional fields
      const templateData = [
        {
          'Employee Number': 'EMP001',
          'First Name': 'John',
          'Middle Name': 'Michael',
          'Last Name': 'Doe',
          'Work Email': 'john.doe@example.com',
          'Personal Email': 'john.doe.personal@gmail.com',
          'Phone': '0722123456',
          'National ID': '12345678',
          'Gender': 'Male',
          'Date of Birth': '1990-01-15',
          'Nationality': 'Kenya',
          'Employment Type': 'Permanent',
          'Hire Date': '2024-01-15',
          'Department': 'DEVELOPMENT',
          'Position': 'Software Developer'
        },
        {
          'Employee Number': 'EMP002',
          'First Name': 'Jane',
          'Middle Name': '',
          'Last Name': 'Smith',
          'Work Email': 'jane.smith@example.com',
          'Personal Email': '',
          'Phone': '0733987654',
          'National ID': '87654321',
          'Gender': 'Female',
          'Date of Birth': '1988-05-20',
          'Nationality': 'Kenya',
          'Employment Type': 'Permanent',
          'Hire Date': '2024-02-01',
          'Department': 'MANAGEMENT',
          'Position': 'HR Manager'
        }
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Employees");
      XLSX.writeFile(wb, "employee_upload_template.xlsx");
    },

    downloadDetailedReport() {
      const report = {
        summary: {
          'Total Records': this.processedRecords,
          'Successfully Imported': this.successCount,
          'Failed': this.failedCount,
          'Skipped': this.skippedCount,
          'Import Date': new Date().toISOString(),
          'Batch Size': this.batchSize,
          'Total Batches': this.totalBatches
        },
        successful: this.successfulRecords,
        skipped: this.skippedRecords,
        failed: this.failedRecords
      };

      // Create Excel workbook with multiple sheets
      const wb = XLSX.utils.book_new();

      // Summary sheet
      const summaryData = Object.entries(report.summary).map(([key, value]) => ({
        'Metric': key,
        'Value': value
      }));
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

      // Successful records sheet
      if (this.successfulRecords.length > 0) {
        const successSheet = XLSX.utils.json_to_sheet(this.successfulRecords);
        XLSX.utils.book_append_sheet(wb, successSheet, "Successful");
      }

      // Skipped records sheet
      if (this.skippedRecords.length > 0) {
        const skippedSheet = XLSX.utils.json_to_sheet(this.skippedRecords);
        XLSX.utils.book_append_sheet(wb, skippedSheet, "Skipped");
      }

      // Failed records sheet
      if (this.failedRecords.length > 0) {
        const failedSheet = XLSX.utils.json_to_sheet(this.failedRecords);
        XLSX.utils.book_append_sheet(wb, failedSheet, "Failed");
      }

      // Download the file
      XLSX.writeFile(wb, `import_report_${new Date().getTime()}.xlsx`);
    },

    gotTo(route) {
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.step-item {
  flex: 1;
  text-align: center;
  position: relative;
}

.step-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 24px;
  color: #6c757d;
  transition: all 0.3s;
}

.step-item.active .step-icon {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(79, 70, 229, 0.4);
}

.step-line {
  height: 4px;
  background: #e2e8f0;
  flex: 1;
  margin-top: 30px;
  transition: all 0.3s ease;
  border-radius: 2px;
}

.step-line.active {
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
}

.dropzone-area {
  border: 2px dashed #dee2e6;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.3s;
}

.dropzone-area:hover {
  border-color: #3788d8;
  background-color: #f8f9fa;
}

.table-responsive {
  max-height: 500px;
  overflow-y: auto;
}

.progress {
  height: 25px;
}

.progress-bar {
  font-size: 14px;
  line-height: 25px;
}

.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Main Layout */
.employee-bulk-upload {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.employee-bulk-upload.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  padding-top: 80px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

/* Modern Page Header */
.modern-page-header {
  background: #ffffff;
  border-radius: 20px;
  padding: 25px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: visible;
}

.modern-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.title-section {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.dashboard-title {
  font-size: clamp(1.25rem, 4vw, 32px);
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  word-wrap: break-word;
  background: linear-gradient(90deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 16px;
  margin: 10px 0 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-subtitle .icon {
  font-size: 18px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.outline {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #4f46e5;
}

.action-btn.outline:hover {
  background: #f8fafc;
  color: #4338ca;
  border-color: #cbd5e1;
}

.action-btn:hover:not(:disabled):not(.outline) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.3);
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.breadcrumb-section {
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  padding: 0;
  list-style: none;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-item:hover {
  color: #4f46e5;
}

.breadcrumb-current {
  color: #94a3b8;
  font-weight: 600;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

.hr-premium-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.hr-premium-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.hr-chart-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}

.hr-chart-body {
  padding: 24px;
}
</style>